import { ClipboardEvent, FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StepActions } from '../components/StepActions';
import { Alert } from '../components/Alert';
import { confirmRegistration, RegistrationApiError } from '../../../../services/registrationApi';

interface CodeStepProps {
  email: string;
  onComplete: () => void;
  onBack: () => void;
}

const CODE_LENGTH = 6;

/** Passo final: confirmação do código de 6 dígitos enviado por e-mail. */
export function CodeStep({ email, onComplete, onBack }: CodeStepProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const code = digits.join('');

  const focusInput = (index: number) => {
    const clamped = Math.max(0, Math.min(CODE_LENGTH - 1, index));
    inputsRef.current[clamped]?.focus();
    inputsRef.current[clamped]?.select();
  };

  const handleChange = (index: number, raw: string) => {
    const value = raw.replace(/\D/g, '');
    if (!value) {
      setDigits((prev) => prev.map((d, i) => (i === index ? '' : d)));
      return;
    }
    setDigits((prev) => {
      const next = [...prev];
      // Distribui múltiplos dígitos (ex.: digitação rápida) a partir do índice.
      value.split('').forEach((ch, offset) => {
        if (index + offset < CODE_LENGTH) next[index + offset] = ch;
      });
      return next;
    });
    focusInput(index + value.length);
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      focusInput(index - 1);
    } else if (e.key === 'ArrowLeft') {
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight') {
      focusInput(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = Array(CODE_LENGTH).fill('');
    pasted.split('').forEach((ch, i) => (next[i] = ch));
    setDigits(next);
    focusInput(pasted.length);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setPending(false);
    if (code.length !== CODE_LENGTH) {
      setError(t('views.registration.code.incomplete', 'Digite os 6 dígitos do código.'));
      return;
    }

    setSubmitting(true);
    try {
      await confirmRegistration({ email, code });
      onComplete();
    } catch (err) {
      // Código correto, mas documento ainda em análise no backend.
      if (err instanceof RegistrationApiError && err.code === 'document_pending') {
        setPending(true);
      } else {
        setError(
          err instanceof RegistrationApiError ? err.message : t('views.registration.code.confirm_error', 'Não foi possível confirmar o código.'),
        );
        // Código inválido/expirado: limpa para nova tentativa.
        setDigits(Array(CODE_LENGTH).fill(''));
        focusInput(0);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Documento em análise: o cadastro NÃO está concluído, então não avança para
  // o passo de sucesso — devolve o usuário ao login para aguardar a aprovação.
  const handlePendingAcknowledge = () => {
    navigate('/login', {
      state: {
        flash: {
          type: 'warning',
          message: t(
            'views.registration.code.pending_flash',
            'Seu documento está em análise. Você receberá um e-mail assim que a titularidade for aprovada e poderá acessar sua conta.',
          ),
        },
      },
    });
  };

  if (pending) {
    return (
      <div className="text-center py-2">
        <div className="mx-auto mb-4 w-[64px] h-[64px] rounded-full bg-[#fff8e1] flex items-center justify-center">
          <i className="fa fa-hourglass-half text-[26px] text-[#f1c40e]" aria-hidden="true"></i>
        </div>
        <h2 className="text-[16px] font-bold text-navy-800 mb-2">{t('views.registration.code.pending_title', 'Código confirmado!')}</h2>
        <p className="text-[13px] text-navy-700/80 leading-[1.6] mb-6 px-2">
          {t('views.registration.code.pending_text', 'Seu documento está em análise. Assim que a titularidade for aprovada, você receberá um aviso por e-mail e poderá acessar sua conta.')}
        </p>
        <button
          type="button"
          onClick={handlePendingAcknowledge}
          className="w-full py-[11px] rounded-[22px] border-0 cursor-pointer text-[13px] font-bold text-navy-900 bg-lime-500 hover:bg-lime-400 transition-colors"
        >
          {t('views.registration.code.understood', 'Entendi')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <p className="text-[13px] text-navy-700/80 leading-[1.6] mb-[18px]">
        <Trans
          i18nKey="views.registration.code.intro"
          defaults="Enviamos um código de 6 dígitos para <1>{{email}}</1>. Digite-o abaixo para confirmar sua conta."
          values={{ email }}
          components={[<strong className="text-navy-800" />, <strong className="text-navy-800" />]}
        />
      </p>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div className="flex justify-center gap-2 mb-4" role="group" aria-label={t('views.registration.code.group_aria', 'Código de confirmação')}>
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputsRef.current[index] = el)}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            aria-label={t('views.registration.code.digit_aria', { defaultValue: 'Dígito {{n}}', n: index + 1 })}
            className="w-[44px] h-[52px] text-center text-[20px] font-bold text-navy-800 bg-white border border-solid border-mist-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500"
          />
        ))}
      </div>

      <p className="text-center text-[11px] text-navy-700/70 mb-1">
        {t('views.registration.code.spam_hint', 'Verifique também a caixa de spam. O código expira em alguns minutos.')}
      </p>

      <StepActions
        submitLabel={t('views.registration.code.submit', 'Confirmar cadastro')}
        loading={submitting}
        loadingLabel={t('views.registration.code.submitting', 'Confirmando...')}
        disabled={code.length !== CODE_LENGTH}
        onBack={onBack}
      />
    </form>
  );
}
